import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { getUserPreferences } from '@/api/userPreferencesApi'
import { getUserOrganizations } from '@/api/organizationApi'
import { assistantsApi } from '@/api/assistants'

/**
 * Hook to load user preferences from server on app startup
 * This replaces localStorage with server-side state sync
 * Also validates that selected_organization_id and currentAssistantId are still accessible
 */
export function useLoadPreferences() {
  const user = useAuthStore((state) => state.user)
  const setCurrentAssistant = useAuthStore((state) => state.setCurrentAssistant)
  const setSelectedOrganizationId = useAuthStore((state) => state.setSelectedOrganizationId)
  const switchToOrganization = useAuthStore((state) => state.switchToOrganization)
  const syncCurrentAssistant = useAuthStore((state) => state.syncCurrentAssistant)
  const validationDone = useRef(false)

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['user-preferences', user?.id],
    queryFn: () => getUserPreferences(user!.id),
    enabled: !!user?.id,
    staleTime: Infinity, // Load once per session
    retry: 1,
  })

  useEffect(() => {
    if (!user) return

    // Load current assistant from preferences
    if (preferences) {
      setCurrentAssistant(preferences.current_assistant_id || null)
    }

    // Load selected organization from user object (always set, even if loading)
    // This ensures we show the correct mode immediately after user loads
    setSelectedOrganizationId(user.selected_organization_id ?? null)

    // Validate membership and assistant consistency (once per session)
    if (!validationDone.current && user.selected_organization_id && preferences) {
      validationDone.current = true

      getUserOrganizations().then(async (orgs) => {
        const isMember = orgs.some((org) => org.id === user.selected_organization_id)

        if (!isMember && orgs.length > 0) {
          // User is no longer a member of selected org → switch org (also resets assistant)
          console.warn('User is no longer a member of selected org, switching to', orgs[0].id)
          await switchToOrganization(orgs[0].id)
          return
        }

        // Organization is valid, but check if assistant belongs to it
        const currentAssistantId = preferences.current_assistant_id
        if (currentAssistantId && user.selected_organization_id) {
          try {
            const assistants = await assistantsApi.getByOrganizationId(user.selected_organization_id)
            const assistantBelongs = assistants.some((a) => a.id === currentAssistantId)
            if (!assistantBelongs) {
              // Assistant doesn't belong to selected org → reset to first available
              console.warn('Current assistant does not belong to selected org, resetting')
              const newAssistantId = assistants.length > 0 ? assistants[0].id : null
              await syncCurrentAssistant(newAssistantId)
            }
          } catch (err) {
            console.error('Failed to validate assistant:', err)
          }
        }
      }).catch(console.error)
    }
  }, [preferences, user, setCurrentAssistant, setSelectedOrganizationId, switchToOrganization, syncCurrentAssistant])

  return { isLoading }
}
